import { useState, memo, useEffect, useCallback, useRef } from 'react';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { useNavigate } from 'react-router-dom';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { 
  Box, 
  InputBase, 
  InputAdornment, 
  ClickAwayListener, 
  Avatar, 
  Typography, 
  Chip,
  Paper,
  Popper,
  CircularProgress
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
// utils
import { bgBlur } from '../../utils/cssStyles';
// components
import Iconify from '../iconify';
import SearchNotFound from '../search-not-found';
import { supabase } from '../supabaseClient';

// ----------------------------------------------------------------------

const StyledSearchInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.5, 0.5, 1.5, 0),
    paddingLeft: `calc(0.25em + ${theme.spacing(1)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    fontSize: '0.875rem',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: '100%',
    },
  },
}));

const StyledPopper = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.modal,
  width: '100%',
  maxWidth: 480,
  position: 'absolute !important',
  '& .MuiAutocomplete-paper': {
    ...bgBlur({ color: theme.palette.background.default }),
    border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
    boxShadow: theme.customShadows.dropdown,
    maxHeight: 420,
  },
  '& .MuiAutocomplete-listbox': {
    padding: theme.spacing(0.5),
    '& .MuiAutocomplete-option': {
      padding: theme.spacing(0.75, 1),
      margin: theme.spacing(0.25, 0),
      borderRadius: theme.shape.borderRadius,
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
      },
      '&[aria-selected="true"]': {
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
      },
    },
  },
}));

const StyledSearchContainer = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.grey[500], 0.08),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${alpha(theme.palette.grey[500], 0.32)}`,
  transition: theme.transitions.create(['background-color', 'box-shadow', 'border-color']),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.text.primary,
  },
  '&:focus-within': {
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.text.primary,
    boxShadow: 'none',
  },
}));


// ----------------------------------------------------------------------

function OmniSearch({ placeholder, size = "medium", onSelect, filterModel, setFilterModel, ...other }) {
  // Generate platform-specific placeholder
  const isMobileWidth = typeof window !== 'undefined' && window.innerWidth < 768;
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const defaultPlaceholder = isMobileWidth 
    ? "Search companies"
    : (isMac ? "Search companies (⌘K)" : "Search companies (Ctrl+K)");
  const finalPlaceholder = placeholder || defaultPlaceholder;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Command+K (Mac) / Ctrl+K (Windows/Linux) shortcut
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if on mobile/tablet width
      const isMobileWidth = window.innerWidth < 768; // Standard tablet breakpoint
      
      // Check for Command+K on Mac or Ctrl+K on Windows/Linux
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const isShortcut = isMac 
        ? (event.metaKey && event.key.toLowerCase() === 'k')
        : (event.ctrlKey && event.key.toLowerCase() === 'k');

      // Only trigger if shortcut is pressed, not already open, and not on mobile
      if (isShortcut && !open && !isMobileWidth) {
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && (
          activeElement.tagName === 'INPUT' || 
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.contentEditable === 'true' ||
          activeElement.isContentEditable
        );
        
        // Don't trigger if user is already typing in an input
        if (!isInputFocused) {
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
          // Focus the input after state update
          setTimeout(() => {
            try {
              if (inputRef.current) {
                inputRef.current.focus();
              } else {
                // Fallback: find the search input by placeholder
                const searchInput = document.querySelector('input[placeholder*="Search"]');
                if (searchInput) {
                  searchInput.focus();
                }
              }
            } catch (error) {
              console.log('Could not focus search input:', error);
            }
          }, 50);
        }
      }
      
      // Close on Escape
      if (event.key === 'Escape' && open) {
        event.preventDefault();
        setOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('keydown', handleKeyDown, true); // Use capture phase
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open]);

  const searchAll = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const allResults = [];

    try {
      // Search companies (main search - always works)
      try {
        const { data: companies, error: companiesError } = await supabase
          .from('companies_processed')
          .select('id, company, description, clearbit_logo, latest_funding_round')
          .or(`company.ilike.%${query}%,description.ilike.%${query}%`)
          .order('company', { ascending: true })
          .limit(6);

        if (companiesError) throw companiesError;

        allResults.push(...companies.map(company => ({
          type: 'company',
          group: 'Companies',
          title: company.company,
          description: company.description,
          path: `/company/${company.id}`,
          logo: company.clearbit_logo,
          fundingRound: company.latest_funding_round,
          id: company.id
        })));
      } catch (error) {
        console.error('Error searching companies:', error);
      }

      // Search keywords (optional - fail silently if table doesn't exist)
      try {
        const { data: keywords } = await supabase
          .from('filter_keywords_list')
          .select('keyword')
          .ilike('keyword', `%${query}%`)
          .order('keyword', { ascending: true })
          .limit(4);

        if (keywords) {
          allResults.push(...keywords.map(keyword => ({
            type: 'keyword',
            group: 'Keywords',
            title: keyword.keyword,
            path: `/tracker/${keyword.keyword}`,
            icon: 'eva:hash-fill'
          })));
        }
      } catch (error) {
        // Silently fail for optional features
        console.log('Keywords search not available:', error.message);
      }

      // Search investors (optional - fail silently if table doesn't exist)
      try {
        const { data: investors } = await supabase
          .from('filter_investor_names')
          .select('name')
          .ilike('name', `%${query}%`)
          .order('name', { ascending: true })
          .limit(4);

        if (investors) {
          allResults.push(...investors.map(investor => ({
            type: 'investor',
            group: 'Investors',
            title: investor.name,
            path: `/investor/${encodeURIComponent(investor.name)}`,
            icon: 'eva:briefcase-fill'
          })));
        }
      } catch (error) {
        // Silently fail for optional features
        console.log('Investors search not available:', error.message);
      }

      // Search locations (optional - fail silently if table doesn't exist)
      try {
        const { data: cities } = await supabase
          .from('filter_cities')
          .select('city')
          .ilike('city', `%${query}%`)
          .order('city', { ascending: true })
          .limit(3);

        if (cities) {
          allResults.push(...cities.map(city => ({
            type: 'city',
            group: 'Cities',
            title: city.city,
            path: `/locations/city/${city.city}`,
            icon: 'eva:pin-fill'
          })));
        }
      } catch (error) {
        // Silently fail for optional features
        console.log('Cities search not available:', error.message);
      }

      try {
        const { data: countries } = await supabase
          .from('filter_countries')
          .select('country')
          .ilike('country', `%${query}%`)
          .order('country', { ascending: true })
          .limit(3);

        if (countries) {
          allResults.push(...countries.map(country => ({
            type: 'country',
            group: 'Countries',
            title: country.country,
            path: `/locations/country/${country.country}`,
            icon: 'eva:globe-fill'
          })));
        }
      } catch (error) {
        // Silently fail for optional features
        console.log('Countries search not available:', error.message);
      }

      setResults(allResults);
    } catch (error) {
      console.error('Error in search:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchAll(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchAll]);

  const handleClick = (path, type, result) => {
    // Always navigate directly to company page for company results
    if (type === 'company') {
      navigate(path);
    } else if (filterModel && setFilterModel) {
      // Apply filters for non-company results
      applyFilter(type, result);
    } else if (path.includes('http')) {
      window.open(path);
    } else {
      navigate(path);
    }
    
    setOpen(false);
    setSearchQuery('');
    
    if (onSelect) {
      onSelect(result);
    }
  };

  const applyFilter = (type, result) => {
    let columnField;
    let operatorValue;
    let value;

    switch (type) {
      case 'company':
        columnField = 'company';
        operatorValue = 'contains';
        value = result.title;
        break;
      case 'keyword':
        columnField = 'keywords';
        operatorValue = 'multicontains';
        value = result.title;
        break;
      case 'investor':
        columnField = 'investors';
        operatorValue = 'multicontains';
        value = result.title;
        break;
      case 'city':
        columnField = 'location_city';
        operatorValue = 'contains';
        value = result.title;
        break;
      case 'country':
        columnField = 'location_country';
        operatorValue = 'contains';
        value = result.title;
        break;
      default:
        return;
    }

    // Remove existing filter for this field to avoid duplicates
    const existingItems = filterModel?.items || [];
    const filteredItems = existingItems.filter(item => item.columnField !== columnField);
    
    // Add new filter
    const newFilter = {
      id: Date.now(),
      columnField,
      operatorValue,
      value
    };

    setFilterModel({
      items: [...filteredItems, newFilter]
    });
  };

  const handleKeyUp = (event) => {
    if (event.key === 'Enter' && results.length > 0) {
      handleClick(results[0].path, results[0].type, results[0]);
    }
  };

  const getIcon = (type, icon) => {
    switch (type) {
      case 'keyword':
        return icon || 'eva:hash-fill';
      case 'investor':
        return icon || 'eva:briefcase-fill';
      case 'city':
      case 'country':
        return icon || 'eva:pin-fill';
      default:
        return 'eva:search-fill';
    }
  };

  const renderSearchPrompt = () => {
    if (searchQuery.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Search for companies, keywords, investors, or locations
          </Typography>
        </Box>
      );
    }
    if (loading) {
      return null; // Don't show anything while loading
    }
    return null;
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: 'relative', width: '100%', maxWidth: { xs: '100%', sm: size === 'small' ? 320 : 480 } }}>
        <Autocomplete
          open={open && (searchQuery.length >= 2 || results.length > 0)}
          onOpen={() => {
            if (searchQuery.length >= 2) {
              setOpen(true);
            }
          }}
          onClose={() => setOpen(false)}
          options={results}
          groupBy={(option) => option.group}
          getOptionLabel={(option) => option.title}
          isOptionEqualToValue={(option, value) => option.path === value.path}
          noOptionsText={loading ? null : (searchQuery.length >= 2 ? <SearchNotFound query={searchQuery} sx={{ py: 2 }} /> : renderSearchPrompt())}
          PopperComponent={(props) => <StyledPopper {...props} placement="bottom-start" />}
          loading={loading}
          filterOptions={(options) => options}
          disablePortal={false}
          forcePopupIcon={false}
          renderInput={(params) => (
            <StyledSearchContainer
              elevation={0}
              {...other}
            >
              <InputAdornment position="start" sx={{ ml: 0.5 }}>
                {loading ? (
                  <CircularProgress size={18} sx={{ color: 'text.disabled' }} />
                ) : (
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                )}
              </InputAdornment>
              <StyledSearchInput
                {...params.InputProps}
                inputRef={inputRef}
                inputProps={{
                  ...params.inputProps,
                  onKeyUp: handleKeyUp,
                }}
                placeholder={finalPlaceholder}
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchQuery(value);
                  if (value.length >= 2) {
                    setOpen(true);
                  } else {
                    setOpen(false);
                  }
                }}
                sx={{ flex: 1 }}
              />
            </StyledSearchContainer>
          )}
          renderOption={(props, option) => {
            const { title, type, description, logo, fundingRound, icon } = option;

            if (type === 'company') {
              const partsTitle = parse(title, match(title, searchQuery));

              return (
                <Box component="li" {...props} onClick={() => handleClick(option.path, option.type, option)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {logo ? (
                      <Avatar
                        src={logo}
                        alt={title}
                        sx={{ width: 28, height: 28, bgcolor: 'background.neutral' }}
                      />
                    ) : (
                      <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
                        <Iconify icon="eva:briefcase-fill" sx={{ color: 'white', width: 14, height: 14 }} />
                      </Avatar>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                          {partsTitle.map((part, index) => (
                            <span
                              key={index}
                              style={{
                                color: part.highlight ? 'inherit' : 'inherit',
                                fontWeight: part.highlight ? 600 : 'inherit',
                                backgroundColor: part.highlight ? 'transparent' : 'transparent',
                                textDecoration: part.highlight ? 'underline' : 'none',
                              }}
                            >
                              {part.text}
                            </span>
                          ))}
                        </Typography>
                        {fundingRound && (
                          <Chip
                            label={fundingRound}
                            size="small"
                            sx={{ 
                              height: 18, 
                              fontSize: '0.65rem',
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main'
                            }}
                          />
                        )}
                      </Box>
                      {description && (
                        <Typography
                          variant="caption"
                          sx={{ 
                            color: 'text.secondary',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 280
                          }}
                        >
                          {description.slice(0, 80)}...
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            }

            // Other result types (keywords, investors, locations)
            const partsTitle = parse(title, match(title, searchQuery));

            return (
              <Box component="li" {...props} onClick={() => handleClick(option.path, option.type, option)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 24, height: 24, bgcolor: 'action.hover' }}>
                    <Iconify icon={getIcon(type, icon)} sx={{ color: 'text.secondary', width: 12, height: 12 }} />
                  </Avatar>
                  <Typography variant="body2">
                    {partsTitle.map((part, index) => (
                      <span
                        key={index}
                        style={{
                          color: part.highlight ? 'inherit' : 'inherit',
                          fontWeight: part.highlight ? 600 : 'inherit',
                          backgroundColor: part.highlight ? 'transparent' : 'transparent',
                          textDecoration: part.highlight ? 'underline' : 'none',
                        }}
                      >
                        {part.text}
                      </span>
                    ))}
                  </Typography>
                </Box>
              </Box>
            );
          }}
        />
      </Box>
    </ClickAwayListener>
  );
}

export default memo(OmniSearch);